import { useId, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { X } from "lucide-react";

import { MAX_UPCS } from "@/api/types";

// Mirrors the API's normalization: digits only, 13-digit zero-padded.
function normalizeUpc(raw: string): string | null {
  const digits = raw.trim();
  if (!/^\d+$/.test(digits)) return null;
  const trimmed = digits.replace(/^0+/, "") || "0";
  return trimmed.length > 13 ? null : trimmed.padStart(13, "0");
}

/**
 * Tag-style UPC entry: Enter or comma turns the typed value into a
 * removable chip (normalized so what you see is what's queried), pasting a
 * comma/space/newline-separated list adds them all, backspace on an empty
 * box removes the last chip. Controlled: `value` is the comma-joined
 * normalized list, so the page can also set it (e.g. a samples button) and
 * the chips follow.
 */
export function UpcListInput({
  value,
  onApply,
  label = "UPCs",
}: {
  value: string;
  onApply: (value: string) => void;
  label?: string;
}) {
  const id = useId();
  const hintId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  const upcs = value ? value.split(",").filter(Boolean) : [];

  /** Add every token in `text` as a chip; keep the first bad one editable. */
  const commit = (text: string) => {
    const tokens = text.split(/[\s,]+/).filter(Boolean);
    if (tokens.length === 0) return;
    const next = [...upcs];
    let bad: string | null = null;
    let full = false;
    for (const token of tokens) {
      const upc = normalizeUpc(token);
      if (!upc) {
        bad ??= token;
        continue;
      }
      if (next.includes(upc)) continue;
      if (next.length >= MAX_UPCS) {
        full = true;
        break;
      }
      next.push(upc);
    }
    setDraft(bad ?? "");
    setProblem(
      bad
        ? `"${bad}" isn't a UPC — digits only, up to 13 of them`
        : full
          ? `At most ${MAX_UPCS} UPCs at a time`
          : null,
    );
    if (next.length !== upcs.length) onApply(next.join(","));
  };

  const remove = (upc: string) => {
    setProblem(null);
    onApply(upcs.filter((u) => u !== upc).join(","));
    inputRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && draft === "" && upcs.length > 0) {
      onApply(upcs.slice(0, -1).join(","));
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    commit(`${draft} ${e.clipboardData.getData("text")}`);
  };

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      {/* The bordered box is the visual input; clicks focus the real one. */}
      <div
        className="flex min-h-9 w-[34rem] max-w-full cursor-text flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1 focus-within:ring-1 focus-within:ring-ring"
        onClick={() => inputRef.current?.focus()}
      >
        {upcs.map((upc) => (
          <span
            key={upc}
            className="inline-flex items-center gap-0.5 rounded-md border py-0.5 pl-2 pr-0.5 text-xs font-medium [font-variant-numeric:tabular-nums]"
            style={{
              background: "var(--viz-meter-track)",
              borderColor: "color-mix(in oklab, var(--viz-series-1) 25%, transparent)",
            }}
          >
            {upc}
            <button
              type="button"
              aria-label={`Remove ${upc}`}
              onClick={(e) => {
                e.stopPropagation();
                remove(upc);
              }}
              className="rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-background/60 hover:text-foreground"
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        ))}
        <input
          id={id}
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={draft}
          aria-describedby={hintId}
          aria-invalid={problem !== null || undefined}
          placeholder={upcs.length === 0 ? "Type or paste UPCs" : "Add another…"}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={() => commit(draft)}
          className="h-6 min-w-28 flex-1 border-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
      <p
        id={hintId}
        className={
          problem ? "text-xs text-destructive" : "text-xs text-muted-foreground"
        }
      >
        {problem ?? "Press Enter to add · paste a list to add them all"}
      </p>
    </div>
  );
}
